import { useEffect } from "react";

/**
 * Detect swipe up/down on a given element and run callbacks.
 * Includes horizontal movement filtering and customizable threshold.
 *
 * @param ref - React ref pointing to the swipe area element
 * @param onSwipeUp - callback for upward swipe
 * @param onSwipeDown - callback for downward swipe
 * @param minSwipeDistance - minimal required movement in px (default: 60)
 * @param maxHorizontalOffset - allowed horizontal jitter in px (default: 30)
 */
export const useSwipeToToggle = (
  ref: React.RefObject<HTMLElement>,
  onSwipeUp: () => void,
  onSwipeDown: () => void,
  minSwipeDistance: number = 60,
  maxHorizontalOffset: number = 30
) => {
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    let startY = 0;
    let endY = 0;
    let startX = 0;
    let endX = 0;

    const onTouchStart = (e: TouchEvent) => {
      startY = e.touches[0].clientY;
      startX = e.touches[0].clientX;
      endY = startY;
      endX = startX;
    };

    const onTouchMove = (e: TouchEvent) => {
      endY = e.touches[0].clientY;
      endX = e.touches[0].clientX;
    };

    const onTouchEnd = () => {
      const verticalDistance = endY - startY;
      const horizontalDistance = Math.abs(endX - startX);

      // Ignore swipes with too much sideways movement
      if (horizontalDistance > maxHorizontalOffset) return;

      // Not enough vertical movement
      if (Math.abs(verticalDistance) < minSwipeDistance) return;

      if (verticalDistance < 0) {
        // Swipe Up
        onSwipeUp();
      } else {
        // Swipe Down
        onSwipeDown();
      }
    };

    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchmove", onTouchMove, { passive: true });
    el.addEventListener("touchend", onTouchEnd);

    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchmove", onTouchMove);
      el.removeEventListener("touchend", onTouchEnd);
    };
  }, [ref, onSwipeUp, onSwipeDown, minSwipeDistance, maxHorizontalOffset]);
};